/**
 * Route Monitoring Service
 * Servico de monitoramento de rotas em tempo real
 *
 * Funcionalidades:
 * - Calculo de ETA dinamico baseado na posicao atual
 * - Deteccao de desvios de rota
 * - Geracao de alertas
 * - Progresso da rota em tempo real
 */

import { calculateRoute } from '../../common/services/routingService';

// =======================================
// Constantes
// =======================================

export const ALERT_TYPES = {
  ROUTE_DEVIATION: 'route_deviation',
  LATE_ARRIVAL: 'late_arrival',
  EARLY_ARRIVAL: 'early_arrival',
  UNPLANNED_STOP: 'unplanned_stop',
  STOP_SKIPPED: 'stop_skipped',
  VEHICLE_STOPPED: 'vehicle_stopped',
  SPEED_VIOLATION: 'speed_violation',
  GEOFENCE_EXIT: 'geofence_exit',
};

export const ALERT_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
};

export const EXECUTION_STATUS = {
  NOT_STARTED: 'not_started',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  CANCELLED: 'cancelled',
};

// Configuracoes de monitoramento
export const MONITORING_CONFIG = {
  // Distancia maxima do corredor da rota (metros)
  MAX_DEVIATION_DISTANCE: 500,

  // Tolerancia para considerar atraso (segundos)
  LATE_ARRIVAL_THRESHOLD: 900, // 15 minutos

  // Distancia para considerar chegada no ponto (metros)
  ARRIVAL_DISTANCE: 100,

  // Tempo parado para considerar parada nao planejada (segundos)
  UNPLANNED_STOP_TIME: 600, // 10 minutos

  // Velocidade minima para considerar em movimento (km/h)
  MIN_MOVING_SPEED: 3,

  // Intervalo de verificacao (ms)
  CHECK_INTERVAL: 5000,
};

// =======================================
// Utilidades Geometricas
// =======================================

/**
 * Calcula distancia haversine entre dois pontos (metros)
 */
export const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371000; // Raio da Terra em metros
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Calcula distancia de um ponto a um segmento de linha
 */
export const pointToLineDistance = (point, lineStart, lineEnd) => {
  const [px, py] = point;
  const [x1, y1] = lineStart;
  const [x2, y2] = lineEnd;

  const A = px - x1;
  const B = py - y1;
  const C = x2 - x1;
  const D = y2 - y1;

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  let param = -1;

  if (lenSq !== 0) {
    param = dot / lenSq;
  }

  let xx, yy;

  if (param < 0) {
    xx = x1;
    yy = y1;
  } else if (param > 1) {
    xx = x2;
    yy = y2;
  } else {
    xx = x1 + param * C;
    yy = y1 + param * D;
  }

  return haversineDistance(py, px, yy, xx);
};

/**
 * Calcula a distancia minima de um ponto a uma rota (polyline)
 */
export const distanceToRoute = (lat, lng, routeCoordinates) => {
  if (!routeCoordinates || routeCoordinates.length < 2) {
    return Infinity;
  }

  let minDistance = Infinity;

  for (let i = 0; i < routeCoordinates.length - 1; i++) {
    const start = routeCoordinates[i];
    const end = routeCoordinates[i + 1];

    // Coordenadas no formato [lng, lat]
    const dist = pointToLineDistance(
      [lng, lat],
      start,
      end
    );

    if (dist < minDistance) {
      minDistance = dist;
    }
  }

  return minDistance;
};

/**
 * Encontra o ponto mais proximo na rota
 */
export const findNearestPointOnRoute = (lat, lng, routeCoordinates) => {
  if (!routeCoordinates || routeCoordinates.length < 2) {
    return null;
  }

  let minDistance = Infinity;
  let nearestPoint = null;
  let segmentIndex = 0;

  for (let i = 0; i < routeCoordinates.length - 1; i++) {
    const [x1, y1] = routeCoordinates[i];
    const [x2, y2] = routeCoordinates[i + 1];

    // Projecao do ponto no segmento
    const A = lng - x1;
    const B = lat - y1;
    const C = x2 - x1;
    const D = y2 - y1;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    let param = lenSq !== 0 ? dot / lenSq : -1;

    let xx, yy;
    if (param < 0) {
      xx = x1;
      yy = y1;
    } else if (param > 1) {
      xx = x2;
      yy = y2;
    } else {
      xx = x1 + param * C;
      yy = y1 + param * D;
    }

    const dist = haversineDistance(lat, lng, yy, xx);

    if (dist < minDistance) {
      minDistance = dist;
      nearestPoint = { lng: xx, lat: yy };
      segmentIndex = i;
    }
  }

  return { point: nearestPoint, distance: minDistance, segmentIndex };
};

// =======================================
// Calculo de ETA
// =======================================

/**
 * Calcula ETA dinamico para a proxima parada
 * @param {Object} currentPosition - Posicao atual do veiculo
 * @param {Object} nextStop - Proxima parada
 * @param {Object} options - Opcoes (velocidade media, etc)
 * @returns {Object} - ETA em segundos e timestamp estimado
 */
export const calculateETA = async (currentPosition, nextStop, options = {}) => {
  const { useOSRM = true, averageSpeed = 40 } = options;

  if (!currentPosition || !nextStop) {
    return null;
  }

  const { latitude: lat1, longitude: lng1 } = currentPosition;
  const { lat: lat2, lng: lng2 } = nextStop.location || nextStop;

  if (!lat1 || !lng1 || !lat2 || !lng2) {
    return null;
  }

  try {
    if (useOSRM) {
      // Usar OSRM para calculo preciso
      const route = await calculateRoute(
        [
          { lat: lat1, lng: lng1 },
          { lat: lat2, lng: lng2 },
        ],
        'car',
        { overview: 'false' }
      );

      const durationSeconds = route.duration;
      const distanceMeters = route.distance;

      return {
        duration: durationSeconds,
        distance: distanceMeters,
        estimatedArrival: new Date(Date.now() + durationSeconds * 1000),
        method: 'osrm',
      };
    }
  } catch (error) {
    console.warn('[ETA] OSRM failed, using fallback:', error.message);
  }

  // Fallback: calculo por distancia haversine e velocidade media
  const distance = haversineDistance(lat1, lng1, lat2, lng2);
  const speedMs = (averageSpeed * 1000) / 3600; // km/h para m/s
  const durationSeconds = Math.ceil(distance / speedMs);

  return {
    duration: durationSeconds,
    distance,
    estimatedArrival: new Date(Date.now() + durationSeconds * 1000),
    method: 'haversine',
  };
};

/**
 * Calcula ETA para todas as paradas restantes
 */
export const calculateAllETAs = async (currentPosition, remainingStops, options = {}) => {
  const etas = [];
  let accumulatedTime = 0;

  for (let i = 0; i < remainingStops.length; i++) {
    const stop = remainingStops[i];
    const prevStop = i > 0 ? remainingStops[i - 1] : null;

    // Calcular ETA a partir da posicao atual ou da parada anterior
    const fromPosition = prevStop
      ? { latitude: prevStop.location?.lat || prevStop.lat, longitude: prevStop.location?.lng || prevStop.lng }
      : currentPosition;

    const eta = await calculateETA(fromPosition, stop, { ...options, useOSRM: i === 0 });

    if (eta) {
      accumulatedTime += eta.duration + (stop.serviceTime || 300);

      etas.push({
        stopId: stop.id,
        stopName: stop.name,
        eta: eta.duration,
        distance: eta.distance,
        estimatedArrival: new Date(Date.now() + accumulatedTime * 1000),
        accumulatedTime,
      });
    }
  }

  return etas;
};

// =======================================
// Deteccao de Desvios
// =======================================

/**
 * Verifica se o veiculo desviou da rota planejada
 */
export const checkRouteDeviation = (currentPosition, routeGeometry, config = MONITORING_CONFIG) => {
  if (!currentPosition || !routeGeometry) {
    return { deviated: false };
  }

  const { latitude, longitude } = currentPosition;

  // Decodificar geometria se for string (polyline encoded)
  let coordinates = routeGeometry;
  if (typeof routeGeometry === 'string') {
    try {
      coordinates = JSON.parse(routeGeometry);
    } catch {
      // Assumir que e uma polyline encoded, precisaria decodificar
      console.warn('[Deviation] Could not parse route geometry');
      return { deviated: false };
    }
  }

  // Se for GeoJSON
  if (coordinates?.type === 'LineString') {
    coordinates = coordinates.coordinates;
  } else if (coordinates?.coordinates) {
    coordinates = coordinates.coordinates;
  }

  const nearest = findNearestPointOnRoute(latitude, longitude, coordinates);

  if (!nearest) {
    return { deviated: false };
  }

  const deviated = nearest.distance > config.MAX_DEVIATION_DISTANCE;

  return {
    deviated,
    distance: nearest.distance,
    maxAllowed: config.MAX_DEVIATION_DISTANCE,
    nearestPoint: nearest.point,
    segmentIndex: nearest.segmentIndex,
  };
};

// =======================================
// Progresso da Rota
// =======================================

/**
 * Calcula o progresso atual da rota
 */
export const calculateRouteProgress = (currentPosition, route, completedStops = []) => {
  if (!route || !route.steps) {
    return { progress: 0, currentStopIndex: 0 };
  }

  const jobSteps = route.steps.filter((s) => s.type === 'job');
  const totalStops = jobSteps.length;
  const completedCount = completedStops.length;

  // Progresso baseado em paradas completadas
  const stopsProgress = totalStops > 0 ? (completedCount / totalStops) * 100 : 0;

  // Encontrar proxima parada
  const nextStopIndex = completedCount;
  const nextStop = jobSteps[nextStopIndex];

  // Calcular distancia ate proxima parada
  let distanceToNext = null;
  if (currentPosition && nextStop?.location) {
    distanceToNext = haversineDistance(
      currentPosition.latitude,
      currentPosition.longitude,
      nextStop.location.lat,
      nextStop.location.lng
    );
  }

  // Verificar se chegou na proxima parada
  const arrivedAtNext = distanceToNext !== null && distanceToNext <= MONITORING_CONFIG.ARRIVAL_DISTANCE;

  return {
    progress: Math.round(stopsProgress),
    totalStops,
    completedStops: completedCount,
    remainingStops: totalStops - completedCount,
    currentStopIndex: nextStopIndex,
    nextStop,
    distanceToNext,
    arrivedAtNext,
    isComplete: completedCount >= totalStops,
  };
};

// =======================================
// Sistema de Alertas
// =======================================

/**
 * Gera alertas baseados no estado atual
 */
export const generateAlerts = (monitoringState, config = MONITORING_CONFIG) => {
  const alerts = [];
  const now = new Date();

  const {
    currentPosition,
    // route, // disponível se necessário
    routeProgress,
    deviation,
    lastMovementTime,
    currentSpeed,
    nextStopETA,
    nextStop,
  } = monitoringState;

  // Alerta de desvio de rota
  if (deviation?.deviated) {
    alerts.push({
      id: `deviation-${Date.now()}`,
      type: ALERT_TYPES.ROUTE_DEVIATION,
      severity: deviation.distance > config.MAX_DEVIATION_DISTANCE * 2 ? ALERT_SEVERITY.ERROR : ALERT_SEVERITY.WARNING,
      message: `Veiculo desviou ${Math.round(deviation.distance)}m da rota planejada`,
      data: {
        distance: deviation.distance,
        maxAllowed: config.MAX_DEVIATION_DISTANCE,
        position: currentPosition,
      },
      timestamp: now,
    });
  }

  // Alerta de atraso
  if (nextStop && nextStopETA && nextStop.estimatedArrival) {
    const originalETA = new Date(nextStop.estimatedArrival);
    const newETA = nextStopETA.estimatedArrival;
    const delaySeconds = (newETA - originalETA) / 1000;

    if (delaySeconds > config.LATE_ARRIVAL_THRESHOLD) {
      alerts.push({
        id: `late-${nextStop.id}-${Date.now()}`,
        type: ALERT_TYPES.LATE_ARRIVAL,
        severity: delaySeconds > config.LATE_ARRIVAL_THRESHOLD * 2 ? ALERT_SEVERITY.ERROR : ALERT_SEVERITY.WARNING,
        message: `Atraso previsto de ${Math.round(delaySeconds / 60)} min para ${nextStop.name || 'proxima parada'}`,
        data: {
          stopId: nextStop.id,
          stopName: nextStop.name,
          originalETA: originalETA,
          newETA: newETA,
          delaySeconds,
        },
        timestamp: now,
      });
    }
  }

  // Alerta de parada nao planejada (veiculo parado por muito tempo)
  if (lastMovementTime && currentSpeed !== undefined) {
    const stoppedDuration = (now - lastMovementTime) / 1000;
    const isNearStop = routeProgress?.distanceToNext <= config.ARRIVAL_DISTANCE;

    if (
      currentSpeed < config.MIN_MOVING_SPEED &&
      stoppedDuration > config.UNPLANNED_STOP_TIME &&
      !isNearStop
    ) {
      alerts.push({
        id: `unplanned-stop-${Date.now()}`,
        type: ALERT_TYPES.UNPLANNED_STOP,
        severity: ALERT_SEVERITY.WARNING,
        message: `Veiculo parado ha ${Math.round(stoppedDuration / 60)} min fora de ponto de entrega`,
        data: {
          stoppedDuration,
          position: currentPosition,
        },
        timestamp: now,
      });
    }
  }

  return alerts;
};

// =======================================
// Classe de Monitoramento
// =======================================

/**
 * Classe para gerenciar o monitoramento de uma rota
 */
export class RouteMonitor {
  constructor(route, vehicle, options = {}) {
    this.route = route;
    this.vehicle = vehicle;
    this.options = {
      ...MONITORING_CONFIG,
      ...options,
    };

    this.state = {
      status: EXECUTION_STATUS.NOT_STARTED,
      completedStops: [],
      alerts: [],
      lastPosition: null,
      lastMovementTime: null,
      startTime: null,
      endTime: null,
    };

    this.listeners = new Set();
  }

  /**
   * Inicia o monitoramento
   */
  start() {
    this.state.status = EXECUTION_STATUS.IN_PROGRESS;
    this.state.startTime = new Date();
    this.emit('started', { time: this.state.startTime });
  }

  /**
   * Para o monitoramento
   */
  stop() {
    this.state.status = EXECUTION_STATUS.COMPLETED;
    this.state.endTime = new Date();
    this.emit('completed', { time: this.state.endTime });
  }

  /**
   * Pausa o monitoramento
   */
  pause() {
    this.state.status = EXECUTION_STATUS.PAUSED;
    this.emit('paused');
  }

  /**
   * Retoma o monitoramento
   */
  resume() {
    this.state.status = EXECUTION_STATUS.IN_PROGRESS;
    this.emit('resumed');
  }

  /**
   * Atualiza a posicao do veiculo
   */
  async updatePosition(position) {
    if (this.state.status !== EXECUTION_STATUS.IN_PROGRESS) {
      return null;
    }

    // const prevPosition = this.state.lastPosition; // disponível para cálculos de velocidade
    this.state.lastPosition = position;

    // Verificar se esta em movimento
    const speed = position.speed || 0;
    if (speed >= this.options.MIN_MOVING_SPEED) {
      this.state.lastMovementTime = new Date();
    }

    // Calcular progresso
    const progress = calculateRouteProgress(
      position,
      this.route,
      this.state.completedStops
    );

    // Verificar desvio
    const deviation = checkRouteDeviation(
      position,
      this.route.geometry,
      this.options
    );

    // Calcular ETA para proxima parada
    let nextStopETA = null;
    if (progress.nextStop) {
      nextStopETA = await calculateETA(position, progress.nextStop, {
        useOSRM: true,
        averageSpeed: this.options.averageSpeed || 40,
      });
    }

    // Gerar alertas
    const newAlerts = generateAlerts({
      currentPosition: position,
      route: this.route,
      routeProgress: progress,
      deviation,
      lastMovementTime: this.state.lastMovementTime,
      currentSpeed: speed,
      nextStopETA,
      nextStop: progress.nextStop,
    }, this.options);

    // Adicionar novos alertas (evitar duplicatas)
    newAlerts.forEach((alert) => {
      const exists = this.state.alerts.some(
        (a) => a.type === alert.type && Date.now() - a.timestamp < 60000
      );
      if (!exists) {
        this.state.alerts.push(alert);
        this.emit('alert', alert);
      }
    });

    // Verificar chegada automatica
    if (progress.arrivedAtNext && progress.nextStop) {
      this.emit('arrived', {
        stop: progress.nextStop,
        time: new Date(),
      });
    }

    const update = {
      position,
      progress,
      deviation,
      nextStopETA,
      alerts: this.state.alerts,
      timestamp: new Date(),
    };

    this.emit('update', update);

    return update;
  }

  /**
   * Marca uma parada como completada
   */
  completeStop(stopId, notes = '') {
    const stop = this.route.steps?.find((s) => s.id === stopId);
    if (!stop) return false;

    this.state.completedStops.push({
      id: stopId,
      completedAt: new Date(),
      notes,
    });

    this.emit('stopCompleted', {
      stop,
      completedAt: new Date(),
      notes,
    });

    // Verificar se todas as paradas foram completadas
    const jobSteps = this.route.steps?.filter((s) => s.type === 'job') || [];
    if (this.state.completedStops.length >= jobSteps.length) {
      this.stop();
    }

    return true;
  }

  /**
   * Pula uma parada
   */
  skipStop(stopId, reason = '') {
    const stop = this.route.steps?.find((s) => s.id === stopId);
    if (!stop) return false;

    this.state.completedStops.push({
      id: stopId,
      skipped: true,
      skippedAt: new Date(),
      reason,
    });

    this.emit('stopSkipped', {
      stop,
      skippedAt: new Date(),
      reason,
    });

    return true;
  }

  /**
   * Adiciona um listener de eventos
   */
  on(event, callback) {
    this.listeners.add({ event, callback });
    return () => this.off(event, callback);
  }

  /**
   * Remove um listener
   */
  off(event, callback) {
    this.listeners.forEach((listener) => {
      if (listener.event === event && listener.callback === callback) {
        this.listeners.delete(listener);
      }
    });
  }

  /**
   * Emite um evento
   */
  emit(event, data) {
    this.listeners.forEach((listener) => {
      if (listener.event === event || listener.event === '*') {
        try {
          listener.callback(data);
        } catch (error) {
          console.error(`[RouteMonitor] Error in listener for ${event}:`, error);
        }
      }
    });
  }

  /**
   * Retorna o estado atual
   */
  getState() {
    return {
      ...this.state,
      route: this.route,
      vehicle: this.vehicle,
    };
  }

  /**
   * Limpa alertas antigos
   */
  clearOldAlerts(maxAge = 300000) {
    const now = Date.now();
    this.state.alerts = this.state.alerts.filter(
      (alert) => now - new Date(alert.timestamp).getTime() < maxAge
    );
  }
}

// =======================================
// Hook de Monitoramento React
// =======================================

/**
 * Cria um hook customizado para usar o RouteMonitor
 * (Sera usado no componente React)
 */
export const createRouteMonitorHook = (route, vehicle) => {
  const monitor = new RouteMonitor(route, vehicle);

  return {
    monitor,
    start: () => monitor.start(),
    stop: () => monitor.stop(),
    updatePosition: (pos) => monitor.updatePosition(pos),
    completeStop: (stopId, notes) => monitor.completeStop(stopId, notes),
    skipStop: (stopId, reason) => monitor.skipStop(stopId, reason),
    getState: () => monitor.getState(),
    on: (event, callback) => monitor.on(event, callback),
  };
};

export default {
  ALERT_TYPES,
  ALERT_SEVERITY,
  EXECUTION_STATUS,
  MONITORING_CONFIG,
  haversineDistance,
  distanceToRoute,
  findNearestPointOnRoute,
  calculateETA,
  calculateAllETAs,
  checkRouteDeviation,
  calculateRouteProgress,
  generateAlerts,
  RouteMonitor,
  createRouteMonitorHook,
};
